import { useRef, useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type ChunkMapData } from "./api";

const COLORS: Record<string, string> = {
    stored: "#33aa55", pending: "#aa8833", downloading: "#aa8833",
    expired: "#333", failed: "#aa3333",
};
const GAP = "#1a1a1a";
const col = (status?: string) => status ? (COLORS[status] || "#222") : GAP;

export function ChunkMap({ streamKey, title, onSeek }: {
    streamKey: string;
    title: string;
    onSeek?: (time: number) => void;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [hover, setHover] = useState("");
    const layoutRef = useRef<{ cellSize: number; cols: number; minSeq: number; vMap: Record<number, string>; aMap: Record<number, string> } | null>(null);

    const { data } = useQuery({
        queryKey: ["chunks", streamKey],
        queryFn: () => api.chunks(streamKey),
        staleTime: 5_000,
        refetchInterval: 10_000,
    });

    const draw = useCallback((data: ChunkMapData) => {
        const canvas = canvasRef.current;
        if (!canvas || !data.chunks.length) return;

        const dpr = window.devicePixelRatio || 1;
        const W = canvas.getBoundingClientRect().width;
        const { min_seq: minSeq, max_seq: maxSeq } = data;
        const totalSeqs = maxSeq - minSeq + 1;

        const cellSize = Math.max(2, Math.min(8, Math.floor(W / Math.sqrt(totalSeqs))));
        const cols = Math.floor(W / cellSize);
        const rows = Math.ceil(totalSeqs / cols);
        const H = rows * cellSize;

        canvas.width = W * dpr;
        canvas.height = H * dpr;
        canvas.style.height = H + "px";

        const ctx = canvas.getContext("2d")!;
        ctx.scale(dpr, dpr);

        const vMap: Record<number, string> = {};
        for (const c of data.chunks) vMap[c.sequence] = c.status;
        const aMap: Record<number, string> = {};
        if (data.audioChunks) for (const c of data.audioChunks) aMap[c.sequence] = c.status;
        const hasAudio = data.audioChunks?.length > 0;

        layoutRef.current = { cellSize, cols, minSeq, vMap, aMap };

        ctx.fillStyle = "#111";
        ctx.fillRect(0, 0, W, H);

        for (let i = 0; i < totalSeqs; i++) {
            const seq = minSeq + i;
            const x = (i % cols) * cellSize;
            const y = Math.floor(i / cols) * cellSize;
            const s = cellSize - 1;
            const vc = col(vMap[seq]);
            const ac = col(aMap[seq]);

            if (!hasAudio || vc === ac) {
                ctx.fillStyle = vc;
                ctx.fillRect(x, y, s, s);
            } else {
                ctx.beginPath();
                ctx.moveTo(x, y); ctx.lineTo(x + s, y); ctx.lineTo(x, y + s); ctx.closePath();
                ctx.fillStyle = vc; ctx.fill();
                ctx.beginPath();
                ctx.moveTo(x + s, y); ctx.lineTo(x + s, y + s); ctx.lineTo(x, y + s); ctx.closePath();
                ctx.fillStyle = ac; ctx.fill();
            }
        }
    }, []);

    useEffect(() => { if (data) draw(data); }, [data, draw]);
    useEffect(() => {
        const resize = () => { if (data) draw(data); };
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, [data, draw]);

    const getSeqFromEvent = (e: React.MouseEvent) => {
        const layout = layoutRef.current;
        if (!layout) return null;
        const rect = canvasRef.current!.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const c = Math.floor(x / layout.cellSize);
        const r = Math.floor(y / layout.cellSize);
        return layout.minSeq + r * layout.cols + c;
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        const seq = getSeqFromEvent(e);
        if (seq === null) return;
        const layout = layoutRef.current!;
        const vs = layout.vMap[seq] || "gap";
        const as = layout.aMap[seq];
        const hasAudio = Object.keys(layout.aMap).length > 0;
        setHover(hasAudio ? `seq ${seq} - video: ${vs} | audio: ${as || "gap"}` : `seq ${seq} - ${vs}`);
    };

    const handleClick = (e: React.MouseEvent) => {
        if (!onSeek || !data) return;
        const seq = getSeqFromEvent(e);
        if (seq === null) return;
        const layout = layoutRef.current!;
        // Find nearest stored chunk
        let target: number | null = null;
        for (let d = 0; d < 20; d++) {
            if (layout.vMap[seq - d] === "stored") { target = seq - d; break; }
            if (layout.vMap[seq + d] === "stored") { target = seq + d; break; }
        }
        if (target === null) return;
        const td = 3; // target duration
        onSeek((target - layout.minSeq) * td);
    };

    return (
        <div className="chunk-map-wrap">
            <h3>chunk map - {title}</h3>
            <canvas ref={canvasRef} height={80} onClick={handleClick} onMouseMove={handleMouseMove} onMouseLeave={() => setHover("")} />
            <div className="chunk-hover">{hover || "\u00A0"}</div>
            <div className="chunk-legend">
                <span className="leg-stored">stored</span>
                <span className="leg-pending">pending</span>
                <span className="leg-expired">expired</span>
                <span className="leg-failed">failed</span>
                <span className="leg-missing">gap</span>
                <span style={{ color: "#666" }}>| split = video/audio</span>
            </div>
        </div>
    );
}
